import type { RegisteredComponent } from "@builder.io/sdk-react";

import { ModelMap } from "~/hec/builder/model-map";
import { articleUrl } from "~/hec/utils/article-urls";
import { useArticleRefTile } from "~/hooks/learning-center/use-article-pagination";

import { ArticleTile } from "./article-tile";

/**
 * SFCC-11498 (ARTICLES-12) — port of composable `articleCard.jsx`.
 *
 * A Builder block that renders a single article tile from a
 * `learning-center-article` reference. Prefers the server-resolved reference
 * `value` (the hosting page loader resolves references via
 * `collectAllUnresolvedReferences` + `fetchReferences`); falls back to a client
 * fetch by reference id when `value` is absent (e.g. the Builder editor canvas),
 * mirroring composable's lazy `useFetchBuilderReference`.
 *
 * `noWrap` block — must spread `attributes` on its root element (done by
 * `ArticleTile`).
 */

interface ArticleRefData {
  id?: string;
  title?: string;
  image?: string;
}

interface ArticleReference {
  id?: string;
  value?: { data?: ArticleRefData };
}

interface ArticleCardProps {
  articleRef?: ArticleReference;
  ctaText?: string;
  attributes?: Record<string, unknown>;
}

const ArticleCard = ({ articleRef, ctaText, attributes }: ArticleCardProps) => {
  // SFCC-11723 — no hardcoded fallback here; `ArticleTile` supplies the
  // localized "Read More" default when `ctaText` is unset. A Builder per-entry
  // `ctaText` override still flows straight through.
  const serverData = articleRef?.value?.data;
  // Only client-fetch when the reference isn't already resolved server-side.
  const { tile: fetchedTile } = useArticleRefTile(
    serverData ? undefined : articleRef?.id,
    "article",
  );

  const slug = serverData?.id;
  const tile =
    slug !== undefined
      ? {
          slug,
          title: serverData?.title ?? "",
          image: serverData?.image ?? "",
          url: articleUrl(slug),
        }
      : fetchedTile;

  if (!tile) return null;

  return (
    <ArticleTile
      image={tile.image}
      title={tile.title}
      url={tile.url}
      ctaText={ctaText}
      attributes={attributes}
    />
  );
};

export const ArticleCardDefinition = {
  component: ArticleCard,
  name: "ArticleCard",
  noWrap: true,
  inputs: [
    {
      name: "articleRef",
      type: "reference",
      model: ModelMap.learningCenterArticle,
      friendlyName: "Select Article",
      required: true,
    },
    {
      name: "ctaText",
      type: "string",
      defaultValue: "Read More",
    },
  ],
} satisfies RegisteredComponent;

export default ArticleCard;
